/**
 * BPReadingCard - Display a single BP reading
 *
 * Features:
 * - Shows date, time, reading type (morning/night)
 * - Displays systolic/diastolic/pulse values
 * - Category badge with color coding
 * - Edit and delete buttons
 * - Notes display (if present)
 */

import React from 'react';
import type { BPReading } from '../../types/bloodPressure';
import { BP_CATEGORY_COLORS, BP_CATEGORY_LABELS, READING_TYPE_ICONS } from '../../types/bloodPressure';

interface BPReadingCardProps {
  reading: BPReading;
  onEdit?: (reading: BPReading) => void;
  onDelete?: (readingId: string) => void;
}

const BPReadingCard: React.FC<BPReadingCardProps> = ({ reading, onEdit, onDelete }) => {
  const categoryColor = BP_CATEGORY_COLORS[reading.category];
  const categoryLabel = BP_CATEGORY_LABELS[reading.category];
  const icon = READING_TYPE_ICONS[reading.reading_type];

  // Format date
  const date = new Date(reading.date);
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="card shadow-soft hover:shadow-elegant transition-shadow duration-300">
      {/* Header: Date and Type */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">{icon}</span>
            <h3 className="text-lg font-semibold text-neutral-800 capitalize">
              {reading.reading_type}
            </h3>
          </div>
          <p className="text-sm text-neutral-500">{dateStr}</p>
        </div>

        {/* Category Badge */}
        <span
          className={`px-3 py-1 rounded-full text-sm font-medium ${categoryColor}`}
        >
          {categoryLabel}
        </span>
      </div>

      {/* BP Values */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-xs text-neutral-500 mb-1">Systolic</p>
          <p className="text-2xl font-bold text-neutral-800">{reading.systolic}</p>
          <p className="text-xs text-neutral-400">mmHg</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 mb-1">Diastolic</p>
          <p className="text-2xl font-bold text-neutral-800">{reading.diastolic}</p>
          <p className="text-xs text-neutral-400">mmHg</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 mb-1">Pulse</p>
          <p className="text-2xl font-bold text-neutral-800">
            {reading.pulse || '-'}
          </p>
          <p className="text-xs text-neutral-400">{reading.pulse ? 'bpm' : ''}</p>
        </div>
      </div>

      {/* Notes */}
      {reading.notes && (
        <div className="mb-4 p-3 bg-neutral-50 rounded-lg">
          <p className="text-xs text-neutral-500 mb-1">Notes</p>
          <p className="text-sm text-neutral-700">{reading.notes}</p>
        </div>
      )}

      {/* Action Buttons */}
      {(onEdit || onDelete) && (
        <div className="flex gap-2 pt-4 border-t border-neutral-200">
          {onEdit && (
            <button
              onClick={() => onEdit(reading)}
              className="flex-1 btn btn-outline text-sm py-2"
            >
              ✏️ Edit
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(reading._id || reading.id)}
              className="flex-1 btn btn-outline text-sm py-2 text-red-600 border-red-300 hover:bg-red-50"
            >
              🗑️ Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BPReadingCard;
